import { useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Box, Alert, Button } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useAdventure } from '../hooks/useAdventures'
import { useActiveSessionForAdventure } from '../hooks/useSession'
import { SessionStatus } from '@api/types/session'
import { LoadingSpinner } from '@components/common/LoadingSpinner'
import AdventureDetailView from './AdventureDetailView'
import AdventurePlayer from '../components/player/AdventurePlayer'

export default function AdventureDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { data: adventure, isLoading, error, refetch } = useAdventure(id!)

  // Track if we've already handled the justCompleted state
  const hasRefetchedRef = useRef(false)

  // Check navigation state
  const locationState = location.state as { sessionId?: string; justCompleted?: boolean; geocacheComplete?: boolean } | null
  const justCompleted = locationState?.justCompleted || locationState?.geocacheComplete
  const passedSessionId = locationState?.sessionId

  // Check if we should show the player (via ?play=true query param or navigation state with sessionId)
  const showPlayerParam = searchParams.get('play') === 'true'
  const showDetailsParam = searchParams.get('details') === 'true'

  // Check for an active session (auto-detect) - only when not explicitly requesting play or details
  const { data: activeSession, isLoading: checkingSession } = useActiveSessionForAdventure(id, {
    enabled: !showPlayerParam && !showDetailsParam && !passedSessionId && !justCompleted,
  })

  // Auto-show player if there's an active in-progress or paused session
  const hasActiveSession = activeSession != null && (
    activeSession.status === SessionStatus.InProgress ||
    activeSession.status === SessionStatus.Paused ||
    activeSession.status === SessionStatus.NotStarted
  )

  // Refetch adventure data when coming from a completed geocache scan
  useEffect(() => {
    if (justCompleted && id && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true
      // Invalidate and refetch the adventure data to get fresh completion status
      queryClient.invalidateQueries({ queryKey: ['adventure', id] })
      refetch()
      // Clear the navigation state to prevent re-refetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [justCompleted, id, queryClient, refetch, navigate, location.pathname])

  // Only block on checkingSession when the query is actually enabled (not forced to details view)
  const shouldCheckSession = !showPlayerParam && !showDetailsParam && !passedSessionId && !justCompleted
  if (isLoading || (shouldCheckSession && checkingSession)) return <LoadingSpinner fullPage />

  if (error || !adventure) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load adventure details. The adventure may not exist or there was a server error.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/adventures')}>
          Back to Adventures
        </Button>
      </Box>
    )
  }

  // If player mode is requested or an active session exists (and details not forced), show the player
  if (!showDetailsParam && (showPlayerParam || passedSessionId || hasActiveSession)) {
    return <AdventurePlayer adventureId={id!} adventure={adventure} />
  }

  // Otherwise, always show the browse/detail view (with option to start/continue adventure)
  return <AdventureDetailView adventure={adventure} />
}
